class B2BCatalogQuantity extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.refreshController = null;
    this.selectedVariantId = null;
    this.hasUnavailableSelection = false;
    this._cartSubmitPending = false;
    this._cartSubmitPendingTimeout = null;
  }

  connectedCallback() {
    this.form = this.closest('form');
    this.input = this.querySelector('.quantity-selector--input, [data-b2b-hidden-quantity]');
    this.minusButton = this.querySelector('.quantity-selector--minus');
    this.plusButton = this.querySelector('.quantity-selector--plus');
    this.pricePerItem = this.querySelector('[data-b2b-price-per-item]');
    this.lastValidQuantity = this.getParsedQuantity(this.input?.value) ?? this.getMinQuantity();

    if (!this.form || !this.input) return;

    this.addEventListener('click', this.handleClickCapture, {
      capture: true,
      signal: this.abortController.signal,
    });
    this.addEventListener('keydown', this.handleKeydownCapture, {
      capture: true,
      signal: this.abortController.signal,
    });
    this.addEventListener('focusout', this.handleFocusOutCapture, {
      capture: true,
      signal: this.abortController.signal,
    });
    this.form.addEventListener('submit', this.handleSubmitCapture, {
      capture: true,
      signal: this.abortController.signal,
    });
    this.form.addEventListener('click', this.handleFormClickCapture, {
      capture: true,
      signal: this.abortController.signal,
    });

    window.addEventListener(`theme:product:${this.controlId}:variantChanged`, this.handleVariantChanged, {
      signal: this.abortController.signal,
    });
    window.addEventListener(`theme:product:${this.controlId}:quantityChanged`, this.handleQuantityChanged, {
      signal: this.abortController.signal,
    });
    window.addEventListener('theme:cart:updated', this.handleCartUpdated, {
      signal: this.abortController.signal,
    });

    this.syncButtonState();
    this.syncPricePerItem();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.refreshController?.abort();
    clearTimeout(this._cartSubmitPendingTimeout);
  }

  get controlId() {
    return this.dataset.controlId;
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get productUrl() {
    return this.dataset.productUrl;
  }

  get isSoldOut() {
    return this.dataset.quantityRuleSoldout === 'true';
  }

  getParsedQuantity(value) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  getMinQuantity() {
    return this.getParsedQuantity(this.input?.getAttribute('min')) ?? 1;
  }

  getMaxQuantity() {
    const value = this.input?.getAttribute('max');
    return value == null || value === '' ? null : this.getParsedQuantity(value);
  }

  getStepQuantity() {
    return this.getParsedQuantity(this.input?.getAttribute('step')) ?? 1;
  }

  getCartQuantity() {
    return this.getParsedQuantity(this.input?.dataset.cartQuantity) ?? 0;
  }

  getCurrentVariantId() {
    return this.form?.querySelector('input[name="id"]')?.value || '';
  }

  getActiveVariantId() {
    if (this.hasUnavailableSelection) return '';

    return this.selectedVariantId || this.getCurrentVariantId();
  }

  handleClickCapture = (event) => {
    const minusButton = event.target.closest('.quantity-selector--minus');
    if (minusButton && this.contains(minusButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.updateByStep(-1);
      return;
    }

    const plusButton = event.target.closest('.quantity-selector--plus');
    if (plusButton && this.contains(plusButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.updateByStep(1);
    }
  };

  handleKeydownCapture = (event) => {
    if (event.key !== 'Enter') return;

    const isManagedButton =
      event.target.closest('.quantity-selector--minus') || event.target.closest('.quantity-selector--plus');

    if (isManagedButton && this.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();

      if (event.target.closest('.quantity-selector--minus')) {
        this.updateByStep(-1);
      } else {
        this.updateByStep(1);
      }
    }
  };

  handleFocusOutCapture = (event) => {
    if (event.target !== this.input) return;

    event.stopPropagation();
    this.sanitizeQuantity({ restoreInvalid: true, dispatch: true });
  };

  handleSubmitCapture = (event) => {
    const busyButton = this.form.querySelector('.product-buy-buttons--primary[aria-busy="true"]');
    if (busyButton) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.refreshController && !this.refreshController.signal.aborted) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isSoldOut) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const nextQuantity = this.sanitizeQuantity({ restoreInvalid: true, dispatch: false });
    if (nextQuantity == null) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.input.value = `${nextQuantity}`;
    this._cartSubmitPending = true;
    clearTimeout(this._cartSubmitPendingTimeout);
    this._cartSubmitPendingTimeout = setTimeout(() => {
      this._cartSubmitPending = false;
    }, 6000);
  };

  handleFormClickCapture = (event) => {
    // Guard the primary Add to Cart button against double-clicks.
    // Theme.js intercepts this button's clicks directly (bypassing the form
    // submit event), so this is the only reliable place to block duplicates.
    const primaryButton = event.target.closest('.product-buy-buttons--primary');
    if (primaryButton && this.form.contains(primaryButton)) {
      if (this._cartSubmitPending || primaryButton.getAttribute('aria-busy') === 'true') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Set the flag synchronously before theme.js can start its AJAX call.
      this._cartSubmitPending = true;
      clearTimeout(this._cartSubmitPendingTimeout);
      this._cartSubmitPendingTimeout = setTimeout(() => {
        this._cartSubmitPending = false;
      }, 6000);
      return;
    }

    const buyNowButton = event.target.closest('.product-buy-buttons--buy-now');
    if (!buyNowButton || !this.form.contains(buyNowButton)) return;

    if (this._cartSubmitPending) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const busyButton = this.form.querySelector('.product-buy-buttons--primary[aria-busy="true"]');
    if (busyButton) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.refreshController && !this.refreshController.signal.aborted) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const nextQuantity = this.sanitizeQuantity({ restoreInvalid: true, dispatch: false });
    if (nextQuantity == null) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.input.value = `${nextQuantity}`;
  };

  handleVariantChanged = (event) => {
    const variantId = event.detail?.variant?.id;

    this.selectedVariantId = variantId ? `${variantId}` : null;
    this.hasUnavailableSelection = !variantId;

    if (this.hasUnavailableSelection) {
      this.refreshController?.abort();
      return;
    }

    window.requestAnimationFrame(() => {
      if (this.isConnected) this.refreshFromServer();
    });
  };

  handleQuantityChanged = () => {
    this.syncButtonState();
    this.syncPricePerItem();
  };

  handleCartUpdated = () => {
    this._cartSubmitPending = false;
    clearTimeout(this._cartSubmitPendingTimeout);
    if (this.isConnected && !this.hasUnavailableSelection) this.refreshFromServer();
  };

  updateByStep(direction) {
    if (this.isSoldOut) return;

    const min = this.getMinQuantity();
    const max = this.getMaxQuantity();
    const step = this.getStepQuantity();
    const current = this.getParsedQuantity(this.input.value);
    const startValue = this.isValidQuantity(current) ? current : this.lastValidQuantity ?? min;

    if (direction < 0 && startValue <= min) {
      this.showMinError();
      return;
    }

    let nextValue = startValue + direction * step;

    if (direction < 0) {
      nextValue = Math.max(min, nextValue);
    } else if (max != null) {
      nextValue = Math.min(max, nextValue);
    }

    if (!this.isValidQuantity(nextValue)) return;

    this.setQuantity(nextValue, true);
  }

  showMinError() {
    const errorEl = this.querySelector('.b2b-qty-min-error');
    if (!errorEl) return;

    this.classList.add('b2b-qty-error');
    errorEl.setAttribute('aria-hidden', 'false');

    clearTimeout(this._minErrorTimeout);
    this._minErrorTimeout = setTimeout(() => {
      errorEl.setAttribute('aria-hidden', 'true');
      this.classList.remove('b2b-qty-error');
    }, 5000);
  }

  isValidQuantity(quantity) {
    if (!Number.isInteger(quantity)) return false;
    if (this.isSoldOut) return false;

    const min = this.getMinQuantity();
    const max = this.getMaxQuantity();
    const step = this.getStepQuantity();

    if (quantity < min) return false;
    if (max != null && quantity > max) return false;

    return (quantity - min) % step === 0;
  }

  sanitizeQuantity({ restoreInvalid, dispatch }) {
    const parsedQuantity = this.getParsedQuantity(this.input.value);

    if (this.isValidQuantity(parsedQuantity)) {
      this.setQuantity(parsedQuantity, dispatch);
      return parsedQuantity;
    }

    if (Number.isFinite(parsedQuantity) && parsedQuantity < this.getMinQuantity()) {
      this.showMinError();
    }

    if (restoreInvalid) {
      return this.restoreLastValidQuantity(dispatch);
    }

    return null;
  }

  restoreLastValidQuantity(dispatchEvent = false) {
    const fallbackQuantity = this.lastValidQuantity ?? this.getMinQuantity();

    if (!this.isValidQuantity(fallbackQuantity)) {
      this.syncButtonState();
      this.syncPricePerItem();
      return null;
    }

    this.setQuantity(fallbackQuantity, dispatchEvent);
    return fallbackQuantity;
  }

  setQuantity(quantity, dispatchEvent) {
    this.lastValidQuantity = quantity;
    this.input.value = `${quantity}`;
    this.syncButtonState();
    this.syncPricePerItem();

    if (dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent(`theme:product:${this.controlId}:quantityChanged`, {
          detail: quantity,
        })
      );
    }
  }

  syncButtonState() {
    const min = this.getMinQuantity();
    const max = this.getMaxQuantity();
    const current = this.getParsedQuantity(this.input.value) ?? this.lastValidQuantity ?? min;
    const disableControls = this.isSoldOut;

    if (this.input) this.input.disabled = disableControls;
    if (this.minusButton) this.minusButton.disabled = disableControls || current <= min;
    if (this.plusButton) this.plusButton.disabled = disableControls || (max != null && current >= max);
  }

  syncPricePerItem() {
    if (!this.pricePerItem) return;

    const totalQuantity = this.getCartQuantity() + (this.getParsedQuantity(this.input.value) ?? this.getMinQuantity());
    const priceTiers = [...this.querySelectorAll('[data-b2b-volume-pricing-item]')]
      .map((item) => {
        const minimumQuantity = this.getParsedQuantity(item.dataset.minimumQuantity);
        const priceMarkup = item.querySelector('[data-b2b-volume-price]')?.innerHTML;

        if (minimumQuantity == null || !priceMarkup) return null;

        return {
          item,
          minimumQuantity,
          priceMarkup,
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.minimumQuantity - right.minimumQuantity);

    if (priceTiers.length === 0) return;

    let activeTier = priceTiers[0];
    priceTiers.forEach((tier) => {
      if (totalQuantity >= tier.minimumQuantity) {
        activeTier = tier;
      }
    });

    priceTiers.forEach((tier) => {
      tier.item.dataset.b2bActive = `${tier.minimumQuantity === activeTier.minimumQuantity}`;
    });
    this.pricePerItem.innerHTML = activeTier.priceMarkup;
  }

  initializePaymentButton() {
    window.requestAnimationFrame(() => {
      window.Shopify?.PaymentButton?.init?.();
    });
  }

  async refreshFromServer() {
    const variantId = this.getActiveVariantId();
    if (!variantId || !this.productUrl || !this.sectionId) return;

    this.refreshController?.abort();
    this.refreshController = new AbortController();

    try {
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('variant', variantId);

      const response = await fetch(url.toString(), { signal: this.refreshController.signal });

      if (!response.ok) return;

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const updatedQuantity = html.getElementById(this.id);
      const controlId = this.controlId;
      const updatedBuyButtons = html.getElementById(`B2B-BuyButtons-${controlId}`);
      let reinitializePaymentButton = false;

      // Replace quantity first so ProductBuyButtons.connectedCallback finds the live input
      if (updatedQuantity) {
        this.replaceWith(updatedQuantity);
      }

      if (updatedBuyButtons) {
        const currentBuyButtons = document.getElementById(`B2B-BuyButtons-${controlId}`);
        if (currentBuyButtons) {
          currentBuyButtons.replaceWith(updatedBuyButtons);
          reinitializePaymentButton = !!updatedBuyButtons.querySelector('.shopify-payment-button');
        }
      }

      if (reinitializePaymentButton) {
        this.initializePaymentButton();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }
  }
}

if (!customElements.get('b2b-catalog-quantity')) {
  customElements.define('b2b-catalog-quantity', B2BCatalogQuantity);
}
